import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { Form } from 'react-final-form';
import { Decorator } from 'final-form';
import { Button, LoadingButton } from 'mdlkit';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledButtonWrapper = styled.div`
  padding-top: ${({ theme }) => theme.space.md}px;
  margin-left: auto;
  margin-right: auto;
`;

interface WizardProps {
  onSubmit: (values: any) => void;
  children?: any;
  initialValues?: object;
  hideNav?: boolean;
  disableSubmit?: boolean;
  spellCheck?: boolean;
}

interface PageProps {
  children?: any;
  validate?: (values: any) => object;
  decorators?: Decorator<object>[];
}

interface State {
  page: number;
}

class Wizard extends PureComponent<WizardProps, State> {
  static Page = ({ children }: PageProps) => children;

  state = {
    page: 0,
  };

  filteredChildren = () => {
    const { children } = this.props;
    return children.filter((child) => child !== null);
  };

  next = () => {
    this.setState((state) => ({
      page: Math.min(state.page + 1, this.filteredChildren().length - 1),
    }));
  };

  previous = () => {
    this.setState((state) => ({
      page: Math.max(state.page - 1, 0),
    }));
  };

  validate = (values) => {
    const { page } = this.state;
    const activePage = React.Children.toArray(this.filteredChildren())[page];
    return activePage.props.validate ? activePage.props.validate(values) : {};
  };

  decorators = (): Decorator<object>[] => {
    const { page } = this.state;
    const activePage = React.Children.toArray(this.filteredChildren())[page];
    return activePage.props.decorators ? activePage.props.decorators : [];
  };

  handleSubmit = (values) => {
    const { onSubmit } = this.props;
    const { page } = this.state;
    const isLastPage =
      page === React.Children.count(this.filteredChildren()) - 1;
    if (isLastPage) {
      return onSubmit(values);
    }

    this.next();
    return undefined;
  };

  render() {
    const { initialValues, hideNav, disableSubmit, spellCheck } = this.props;
    const { page } = this.state;
    const filtered = this.filteredChildren();
    const activePage = React.Children.toArray(filtered)[page];
    const isLastPage = page === React.Children.count(filtered) - 1;

    return (
      <Form
        initialValues={initialValues}
        validate={this.validate}
        onSubmit={this.handleSubmit}
        decorators={this.decorators()}
      >
        {({ handleSubmit, submitting, valid, values }) => (
          <StyledForm onSubmit={handleSubmit} spellCheck={spellCheck}>
            {activePage}
            {!hideNav && (
              <StyledButtonWrapper>
                {page > 0 && (
                  <Button
                    size="large"
                    mr="sm"
                    type="button"
                    data-testid="backBtn"
                    onClick={this.previous}
                  >
                    Back
                  </Button>
                )}
                {!isLastPage && (
                  <Button
                    size="large"
                    type="submit"
                    data-testid="nextBtn"
                    onClick={this.next}
                    disabled={!valid}
                    data-track-event
                    data-track={JSON.stringify({
                      controlName: 'next-coverage-button',
                      controlText: 'Next',
                    })}
                  >
                    Next
                  </Button>
                )}
                {isLastPage && (
                  <LoadingButton
                    size="large"
                    type="submit"
                    disabled={!valid || submitting || disableSubmit}
                    data-testid="submitBtn"
                    isLoading={submitting || disableSubmit}
                    data-track-event
                    data-track={JSON.stringify({
                      controlName: 'submit-insuranceid-button',
                      controlText: 'Submit',
                      ...((values as any).isDependent
                        ? {
                            attributes: [
                              {
                                key: 'dependentChoice',
                                value:
                                  (values as any).isDependent === 'true'
                                    ? 'Yes'
                                    : 'No',
                              },
                            ],
                          }
                        : {}),
                    })}
                  >
                    Submit
                  </LoadingButton>
                )}
              </StyledButtonWrapper>
            )}
          </StyledForm>
        )}
      </Form>
    );
  }
}

export default Wizard;
